import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  Collapse,
  Divider,
  Grid,
  Avatar,
  Box,
  useTheme,
  useMediaQuery,
  Paper,
  Tooltip,
} from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';

import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  School as SchoolIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  ExpandLess,
  ExpandMore,
  Payment as PaymentIcon,
  Business as BranchIcon,
  Person as TeacherIcon,
  Group as StudentsIcon,
  LibraryBooks as LibraryBooksIcon,
  AddBox as AddBoxIcon,
  FormatListBulleted as FormatListBulletedIcon,
  Assignment as AssignmentIcon,
  LockReset as LockResetIcon,
  AccountCircle as AccountCircleIcon,
  ContactPhone as ContactPhoneIcon,
  LocationCity as LocationCityIcon,
} from '@mui/icons-material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { styled, keyframes } from '@mui/system';
import { motion } from 'framer-motion';
// ==== ÖRNEK İçerikler (Burada siz kendi içeriklerinizi import edeceksiniz) ====
import OkullarContent from './Okullar';
import Ogretmen from './ogretmen/Ogretmen';
import Ogrenci from './ogrenci/Ogrenci';
import BookUploadForm from './ogretmen/kutuphane/Add';
import BookListe from './ogretmen/kutuphane/liste/books/AllListe';
import BenimListe from './ogretmen/kutuphane/liste/books/BenimListem';
import BenimTestler from './ogretmen/kutuphane/liste/tests/BenimListem';
import TumTestler from './ogretmen/kutuphane/liste/tests/TumListe';
import Password from './ayarlar/Password';
import Profil from './ayarlar/Profil';
import useFetchUserData from '../../hooks/kurum/useFetchUserData';
import { API_URLS } from '../../config/config';
import PriceManagement from './kampanya/PriceManagement';
import PaymentList from './payment/PaymentList';

/** ====== Drawer Genişliği ====== **/
const drawerWidth = 240;

/** ====== Animasyonlar ====== **/
const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  20% { background-position: 25% 50%; }
  40% { background-position: 50% 50%; }
  60% { background-position: 75% 50%; }
  80% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const iconFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`;

/** 
 * Gradients for different card variants (AdminDashboard'tan aldığımız)
**/
const getGradient = (variant) => {
  switch (variant) {
    case 'payment-daily':
      return 'linear-gradient(-45deg, #FF4500, #FF6347, #FF7F50, #FF8C00, #FF0000)';
    case 'payment-weekly':
      return 'linear-gradient(-45deg, #FF8C00, #FFA500, #FFB347, #FF6347, #FF4500)';
    case 'payment-monthly':
      return 'linear-gradient(-45deg, #FFD700, #FFA500, #FF8C00, #FF6347, #FF4500)';
    case 'student':
      return 'linear-gradient(-45deg, #00BFFF, #1E90FF, #4169E1, #0000CD, #000080)';
    case 'school':
      return 'linear-gradient(-45deg, #3CB371, #2E8B57, #228B22, #006400, #00FF7F)';
    case 'bayi':
      return 'linear-gradient(-45deg, #8A2BE2, #9932CC, #9400D3, #800080, #4B0082)';
    case 'teacher':
      return 'linear-gradient(-45deg, #FF1493, #FF69B4, #FFB6C1, #C71585, #DB7093)';
    default:
      // fallback gradient
      return 'linear-gradient(-45deg, #FF4500, #FF6347, #FF7F50, #FF8C00, #FF0000)';
  }
};

/** ====== Stil Kart (Animated Gradient Card) ====== **/
const StyledCard = styled(Paper)(({ variant }) => ({
  borderRadius: 16,
  padding: '16px',
  color: '#fff',
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
  backdropFilter: 'blur(15px)',
  background: getGradient(variant),
  backgroundSize: '600% 600%',
  animation: `${gradientAnimation} 10s ease infinite`,
  transition: 'transform 0.4s ease, box-shadow 0.4s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 12px 32px rgba(0,0,0,0.3)',
    '& svg': {
      animation: `${iconFloat} 1s ease-in-out infinite`,
    },
  },
}));

/** ====== Main Content Alanı (Drawer Aç/Kapa Animasyonu Dahil) ====== **/
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.standard,
    }),
    marginLeft: open ? `${drawerWidth}px` : 0,
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
    },
    marginTop: '64px',
    overflow: 'auto',
    height: `calc(100vh - 64px)`,
    backgroundColor: '#f0e7ff',
  })
);

/** ====== AppBar (Üst Menu) Style ====== **/
const AppBarStyled = styled((props) => <AppBar {...props} />)(({ theme, open }) => ({
  backgroundColor: '#6a1b9a',
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.standard,
  }),
  ...(open && {
    [theme.breakpoints.up('sm')]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: `${drawerWidth}px`,
    },
  }),
}));

/** ====== Drawer Style ====== **/
const DrawerStyled = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    background: 'linear-gradient(135deg, #4a148c 0%, #6a1b9a 100%)',
    color: '#fff',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.standard,
    }),
  },
}));

/** ====== Framer Motion Variant ====== **/
const containerVariants = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -50 },
};

/** ====== Currency Formatter (TRY) ====== **/
const formatter = new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  minimumFractionDigits: 2,
});

function KurumsalDashboard() {
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [userData, setUserData] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState('Anasayfa');
  const [open, setOpen] = useState({});
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ====== ADMIN'DEKİ CARDLAR İÇİN GEREKEN STATE'LER VE useEffect'ler ======
  const [paymentSummary, setPaymentSummary] = useState(null);

  const [teacherCounts, setTeacherCounts] = useState({
    totalOgretmen: 0,
    activeOgretmen: 0,
    inactiveOgretmen: 0,
  });
  const [loadingTeacherCounts, setLoadingTeacherCounts] = useState(true);

  const [studentCounts, setStudentCounts] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });
  const [loadingStudentCounts, setLoadingStudentCounts] = useState(true);

  const [schoolCounts, setSchoolCounts] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });
  const [loadingSchoolCounts, setLoadingSchoolCounts] = useState(true);

  const [kurumCounts, setKurumCounts] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });
  const [loadingKurumCounts, setLoadingKurumCounts] = useState(true);

  // Admin Dashboard örneğinde olduğu gibi, userData'yı çektiğiniz custom hook
  useFetchUserData(setUserData);

  // Öğretmen sayısı çekme
  useEffect(() => {
    const fetchTeacherCounts = async () => {
      try {
        const response = await fetch(`${API_URLS.ADMIN_URL}teachers/count`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('userToken')}`,
            'Content-Type': 'application/json',
          },
        });
        const data = await response.json();
        if (response.ok) {
          setTeacherCounts({
            totalOgretmen: data.totalOgretmen,
            activeOgretmen: data.activeOgretmen,
            inactiveOgretmen: data.inactiveOgretmen,
          });
        } else {
          console.error('Öğretmen sayıları alınamadı:', data.message);
        }
      } catch (error) {
        console.error('Öğretmen sayıları alınırken bir hata oluştu:', error);
      } finally {
        setLoadingTeacherCounts(false);
      }
    };
    fetchTeacherCounts();
  }, []);

  // Öğrenci sayısı çekme
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const response = await fetch(`${API_URLS.ADMIN_URL}student/total`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('userToken')}`,
            'Content-Type': 'application/json',
          },
        });
        const data = await response.json();
        if (response.ok) {
          setStudentCounts({
            total: data.total,
            active: data.active,
            inactive: data.inactive,
          });
        } else {
          console.error('Toplam öğrenci alınamadı:', data.message);
        }
      } catch (error) {
        console.error('Toplam öğrenci alınırken bir hata oluştu:', error);
      } finally {
        setLoadingStudentCounts(false);
      }
    };
    fetchStudents();
  }, []);

  // Okul sayısı çekme
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const response = await fetch(`${API_URLS.ADMIN_URL}schools/total`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('userToken')}`,
            'Content-Type': 'application/json',
          },
        });
        const data = await response.json();
        if (response.ok) {
          setSchoolCounts({
            total: data.total,
            active: data.active,
            inactive: data.inactive,
          });
        } else {
          console.error('Toplam okul sayısı alınamadı:', data.message);
        }
      } catch (error) {
        console.error('Toplam okul sayısı alınırken bir hata oluştu:', error);
      } finally {
        setLoadingSchoolCounts(false);
      }
    };
    fetchSchools();
  }, []);

  // Bayi (kurum) sayısı çekme
  useEffect(() => {
    const fetchKurums = async () => {
      try {
        const response = await fetch(`${API_URLS.ADMIN_URL}kurumsal/total`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('userToken')}`,
            'Content-Type': 'application/json',
          },
        });
        const data = await response.json();
        if (response.ok) {
          setKurumCounts({
            total: data.total,
            active: data.active,
            inactive: data.inactive,
          });
        } else {
          console.error('Toplam kurum (bayi) sayısı alınamadı:', data.message);
        }
      } catch (error) {
        console.error('Toplam kurum (bayi) sayısı alınırken bir hata oluştu:', error);
      } finally {
        setLoadingKurumCounts(false);
      }
    };
    fetchKurums();
  }, []);

  // Payment summary çekme
  useEffect(() => {
    const fetchPayments = async () => {
      try {
        const response = await fetch(`${API_URLS.ADMIN_URL}payments/summary`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('userToken')}`,
          },
        });
        const data = await response.json();
        if (response.ok) {
          setPaymentSummary(data);
        } else {
          console.error('Toplam ödemeler alınamadı:', data.message);
        }
      } catch (error) {
        console.error('Toplam ödemeler alınırken bir hata oluştu:', error);
      }
    };
    fetchPayments();
  }, []);

  // Aktif-Pasif gösterimi
  const renderAktifPasif = (aktifSayi, pasifSayi) => (
    <Box mt={2} display="flex" justifyContent="space-around" width="100%">
      <Box textAlign="center">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Aktif
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {aktifSayi}
        </Typography>
      </Box>
      <Box textAlign="center">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Pasif
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {pasifSayi}
        </Typography>
      </Box>
    </Box>
  );

  // ========== Drawer Aç/Kapa ==========
  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  // ========== Alt menü Aç/Kapa ==========
  const handleClick = (menu) => {
    setOpen((prevOpen) => ({ ...prevOpen, [menu]: !prevOpen[menu] }));
  };

  // ========== Çıkış işlemi ==========
  const handleLogout = async () => {
    try {
      await fetch(API_URLS.KURUMSAL_URL + 'logout', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('userToken')}`,
          'Content-Type': 'application/json',
        },
      });
      localStorage.removeItem('userToken');
      navigate('/kurumsal-giris');
    } catch (error) {
      console.error('Çıkış işlemi sırasında bir hata oluştu', error);
    }
  };

  // ========== Menü itemları ==========
  const menuItems = [
    {
      text: 'Anasayfa',
      icon: <DashboardIcon />,
      action: () => setSelectedMenu('Anasayfa'),
    },
    {
      text: 'Ödemeler',
      icon: <PaymentIcon />,
      action: () => setSelectedMenu('Ödemeler'),
    },
    {
      text: 'Kampanyalar',
      icon: <PaymentIcon />,
      action: () => setSelectedMenu('Kampanyalar'),
    },
    {
      text: 'Kurumlar',
      icon: <SchoolIcon />,
      subMenus: [
        {
          text: 'Okullar',
          icon: <BranchIcon />,
          action: () => setSelectedMenu('Okullar'),
        },
      ],
      action: () => handleClick('Kurumlar'),
    },
    {
      text: 'Öğretmenler',
      icon: <TeacherIcon />,
      action: () => setSelectedMenu('Öğretmenler'),
    },
    {
      text: 'Öğrenciler',
      icon: <StudentsIcon />,
      action: () => setSelectedMenu('Öğrenciler'),
    },
    {
      text: 'Kütüphane',
      icon: <LibraryBooksIcon />,
      subMenus: [
        {
          text: 'Kitap Ekle',
          icon: <AddBoxIcon />,
          action: () => setSelectedMenu('KitapEkle'),
        },
        {
          text: 'Liste',
          icon: <FormatListBulletedIcon />,
          subMenus: [
            {
              text: 'Benim Listem',
              icon: <FormatListBulletedIcon />,
              action: () => setSelectedMenu('BenimListem'),
            },
            {
              text: 'Tüm Liste',
              icon: <FormatListBulletedIcon />,
              action: () => setSelectedMenu('KitapListe'),
            },
          ],
          action: () => handleClick('Liste'),
        },
        {
          text: 'Testler',
          icon: <AssignmentIcon />,
          subMenus: [
            {
              text: 'Benim Testlerim',
              icon: <AssignmentIcon />,
              action: () => setSelectedMenu('BenimTestler'),
            },
            {
              text: 'Tüm Testler',
              icon: <AssignmentIcon />,
              action: () => setSelectedMenu('TumTestler'),
            },
          ],
          action: () => handleClick('Testler'),
        },
      ],
      action: () => handleClick('Kütüphane'),
    },
    {
      text: 'Ayarlar',
      icon: <SettingsIcon />,
      subMenus: [
        {
          text: 'Şifre Güncelle',
          icon: <LockResetIcon />,
          action: () => setSelectedMenu('SifreSet'),
        },
        {
          text: 'Profil',
          icon: <AccountCircleIcon />,
          action: () => setSelectedMenu('Profil'),
        },
      ],
      action: () => handleClick('Ayarlar'),
    },
    {
      text: 'Çıkış Yap',
      icon: <LogoutIcon />,
      action: handleLogout,
    },
  ];

  // ========== İçerik Ayırıcı (Route benzeri switch) ==========
  const renderContent = () => {
    if (!userData) {
      return <Typography paragraph>Yükleniyor...</Typography>;
    }

    switch (selectedMenu) {
      case 'Ödemeler':
        return <PaymentList />;

      case 'Kampanyalar':
        return <PriceManagement />;

      case 'Okullar':
        return <OkullarContent />;

      case 'Öğretmenler':
        return <Ogretmen />;

      case 'Öğrenciler':
        return <Ogrenci />;

      case 'KitapEkle':
        return <BookUploadForm />;

      case 'KitapListe':
        return <BookListe />;

      case 'BenimListem':
        return <BenimListe />;

      case 'BenimTestler':
        return <BenimTestler />;

      case 'TumTestler':
        return <TumTestler />;

      case 'SifreSet':
        return <Password />;

      case 'Profil':
        return <Profil />;

      /** 
       * ================ 
       * Sadece AdminDashboard'taki kart listesini klonladığımız "Anasayfa" 
       * ================
       **/
      default:
        return (
          <>
            <Typography variant="h4" gutterBottom>
              Yönetim Paneli
            </Typography>
            <Typography variant="body1" paragraph>
              Hoşgeldiniz, {userData.fullname}.<br />
              {/* İsterseniz roller/access bilgisi gösterebilirsiniz. */}
            </Typography>
            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6} md={4}>
                <StyledCard variant="payment-daily" onClick={() => setSelectedMenu('Ödemeler')}>
                  <PaymentIcon sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Günlük Toplam Ödeme
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 900, mt: 1 }}>
                    {paymentSummary
                      ? `${formatter.format(paymentSummary.dailyTotal)}`
                      : 'Yükleniyor...'}
                  </Typography>
                </StyledCard>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <StyledCard variant="payment-weekly" onClick={() => setSelectedMenu('Ödemeler')}>
                  <PaymentIcon sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Haftalık Toplam Ödeme
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 900, mt: 1 }}>
                    {paymentSummary
                      ? `${formatter.format(paymentSummary.weeklyTotal)}`
                      : 'Yükleniyor...'}
                  </Typography>
                </StyledCard>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <StyledCard variant="payment-monthly" onClick={() => setSelectedMenu('Ödemeler')}>
                  <PaymentIcon sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Aylık Toplam Ödeme
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 900, mt: 1 }}>
                    {paymentSummary
                      ? `${formatter.format(paymentSummary.monthlyTotal)}`
                      : 'Yükleniyor...'}
                  </Typography>
                </StyledCard>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <StyledCard variant="student" onClick={() => setSelectedMenu('Öğrenciler')}>
                  <StudentsIcon sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Toplam Öğrenci
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 900, mt: 1 }}>
                    {loadingStudentCounts ? 'Yükleniyor...' : studentCounts.total}
                  </Typography>
                  {!loadingStudentCounts &&
                    renderAktifPasif(studentCounts.active, studentCounts.inactive)}
                </StyledCard>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <StyledCard variant="school" onClick={() => setSelectedMenu('Okullar')}>
                  <SchoolIcon sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Toplam Okul
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 900, mt: 1 }}>
                    {loadingSchoolCounts ? 'Yükleniyor...' : schoolCounts.total}
                  </Typography>
                  {!loadingSchoolCounts &&
                    renderAktifPasif(schoolCounts.active, schoolCounts.inactive)}
                </StyledCard>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <StyledCard variant="bayi" onClick={() => setSelectedMenu('Bayiler')}>
                  <BusinessIcon sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Toplam Bayi
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 900, mt: 1 }}>
                    {loadingKurumCounts ? 'Yükleniyor...' : kurumCounts.total}
                  </Typography>
                  {!loadingKurumCounts &&
                    renderAktifPasif(kurumCounts.active, kurumCounts.inactive)}
                </StyledCard>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <StyledCard variant="teacher" onClick={() => setSelectedMenu('Öğretmenler')}>
                  <TeacherIcon sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Toplam Öğretmen
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 900, mt: 1 }}>
                    {loadingTeacherCounts ? 'Yükleniyor...' : teacherCounts.totalOgretmen}
                  </Typography>
                  {!loadingTeacherCounts &&
                    renderAktifPasif(
                      teacherCounts.activeOgretmen,
                      teacherCounts.inactiveOgretmen
                    )}
                </StyledCard>
              </Grid>
            </Grid>
          </>
        );
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBarStyled position="fixed" open={drawerOpen}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            edge="start"
            sx={{ mr: 2, ...(drawerOpen && { display: 'none' }) }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ fontFamily: 'Roboto, sans-serif' }}>
            Kurumsal Panel
          </Typography>
        </Toolbar>
      </AppBarStyled>

      {/* Yan Menü (Drawer) */}
      <DrawerStyled
        variant={isMobile ? 'temporary' : 'persistent'}
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <Toolbar />
        <Divider />
        <List>
          {menuItems.map((item, index) => (
            <React.Fragment key={index}>
              <ListItem
                button
                onClick={() => {
                  item.action && item.action();
                  if (isMobile && !item.subMenus) toggleDrawer();
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: '#8e24aa',
                  },
                  backgroundColor: selectedMenu === item.text ? '#8e24aa' : 'inherit',
                  transition: 'background-color 0.3s',
                }}
              >
                <ListItemIcon sx={{ color: '#fff' }}>
                  <Tooltip title={item.text} placement="right" disableHoverListener={!isMobile}>
                    {item.icon}
                  </Tooltip>
                </ListItemIcon>
                <ListItemText primary={item.text} />
                {item.subMenus ? (open[item.text] ? <ExpandLess /> : <ExpandMore />) : null}
              </ListItem>

              {/* Alt Menüleri varsa göster */}
              {item.subMenus && (
                <Collapse in={open[item.text]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subMenus.map((subItem, subIndex) => (
                      <React.Fragment key={subIndex}>
                        <ListItem
                          button
                          onClick={() => {
                            subItem.action && subItem.action();
                            if (isMobile && !subItem.subMenus) toggleDrawer();
                          }}
                          sx={{
                            pl: 4,
                            '&:hover': {
                              backgroundColor: '#8e24aa',
                            },
                            backgroundColor:
                              selectedMenu === subItem.text ? '#8e24aa' : 'inherit',
                            transition: 'background-color 0.3s',
                          }}
                        >
                          <ListItemIcon sx={{ color: '#fff' }}>
                            <Tooltip
                              title={subItem.text}
                              placement="right"
                              disableHoverListener={!isMobile}
                            >
                              {subItem.icon}
                            </Tooltip>
                          </ListItemIcon>
                          <ListItemText primary={subItem.text} />
                          {subItem.subMenus
                            ? open[subItem.text]
                              ? <ExpandLess />
                              : <ExpandMore />
                            : null}
                        </ListItem>

                        {/* Daha alt seviye subMenus varsa */}
                        {subItem.subMenus && (
                          <Collapse in={open[subItem.text]} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                              {subItem.subMenus.map((nestedItem, nestedIndex) => (
                                <ListItem
                                  button
                                  key={nestedIndex}
                                  onClick={() => {
                                    nestedItem.action && nestedItem.action();
                                    if (isMobile) toggleDrawer();
                                  }}
                                  sx={{
                                    pl: 6,
                                    '&:hover': {
                                      backgroundColor: '#8e24aa',
                                    },
                                    backgroundColor:
                                      selectedMenu === nestedItem.text ? '#8e24aa' : 'inherit',
                                    transition: 'background-color 0.3s',
                                  }}
                                >
                                  <ListItemIcon sx={{ color: '#fff' }}>
                                    <Tooltip
                                      title={nestedItem.text}
                                      placement="right"
                                      disableHoverListener={!isMobile}
                                    >
                                      {nestedItem.icon}
                                    </Tooltip>
                                  </ListItemIcon>
                                  <ListItemText primary={nestedItem.text} />
                                </ListItem>
                              ))}
                            </List>
                          </Collapse>
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </DrawerStyled>

      {/* İçerik Alanı */}
      <Main open={drawerOpen}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {renderContent()}
        </motion.div>
      </Main>
    </Box>
  );
}

export default KurumsalDashboard;
